import { Flex, Form, Input, message } from "antd";
import { FormWrapper, StyledButton } from "../../components/styles";
import { Description, GeneralSettingsContainer, MainFormWrapper, Title } from "./styles"
import { useEffect, useState } from "react";
import { selectSchedulerSettings } from "../../redux/features/schedulerSettings/schedulerSettingsSlice";
import { fetchSchedulerInterval, updateSchedulerInterval, runReviewToInProcessNow } from "../../redux/features/schedulerSettings/schedulerSettingsAsyncThunk";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { RequestResults } from "../../redux/common/RequestStatus";

const SchedulerSettings: React.FC = () => {
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const { data, loading } = useAppSelector(selectSchedulerSettings);

    useEffect(() => {
        dispatch(fetchSchedulerInterval());
    }, [dispatch]);

    useEffect(() => {
        form.setFieldsValue({
            openToPendingMinutes: data.openToPendingMinutes || 0,
            pendingToReviewMinutes: data.pendingToReviewMinutes || 0,
            reviewToInProcessMinutes: data.reviewToInProcessMinutes || 0
        });
    }, [data, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            await dispatch(updateSchedulerInterval(values));
        } catch (errorInfo) {
            console.error("Validation failed:", errorInfo);
        }
    };

    // Manual Review→InProcess trigger. The hourly cron only fires at minute 0, so after
    // deploys or after bumping recentPaymentHighlightHours, admins would otherwise wait
    // up to 60 min for eligible Review claims to migrate. This button calls the same
    // handler the cron uses and surfaces the result (moved count / disabled reason / error).
    const [runNowLoading, setRunNowLoading] = useState(false);
    const handleRunReviewToInProcessNow = async () => {
        setRunNowLoading(true);
        try {
            const result: any = await dispatch(runReviewToInProcessNow()).unwrap();
            message.success(result?.message || `Promoted ${result?.moved ?? 0} claim(s) from Review to In Process.`);
        } catch (err: any) {
            message.error(typeof err === 'string' ? err : err?.message || 'Failed to run Review → InProcess promotion.');
        } finally {
            setRunNowLoading(false);
        }
    };

    return (
        <>
            <GeneralSettingsContainer>
                <Title>Scheduler Settings</Title>
                <Description>Configure automated status transitions with scheduling intervals.</Description>

                <MainFormWrapper>
                    <FormWrapper form={form} layout="vertical">
                        <Form.Item
                            label={<>Open to Review Delay <span>(in minutes, max 1 week = 10080)</span></>}
                            name="openToPendingMinutes"
                            rules={[
                                { required: true, message: "Please enter the delay in minutes!" },
                                {
                                    validator: (_, value) => {
                                        if (value === undefined || value === "") return Promise.resolve();
                                        const number = Number(value);
                                        if (isNaN(number) || number < 0 || number > 10080) {
                                            return Promise.reject("Please enter a number between 0 and 10080 (1 week)");
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                            tooltip="Automatically move items from Open to Review status after specified minutes (0 = disabled, max 10080 = 1 week)"
                        >
                            <Input
                                placeholder="1440"
                                size="large"
                                type="number"
                                min={0}
                                max={10080}
                            />
                        </Form.Item>

                        <Form.Item
                            label={<>Pending to Review Delay <span>(Legacy - drains grandfathered Pending claims)</span></>}
                            name="pendingToReviewMinutes"
                            rules={[
                                { required: true, message: "Please enter the delay in minutes!" },
                                {
                                    validator: (_, value) => {
                                        if (value === undefined || value === "") return Promise.resolve();
                                        const number = Number(value);
                                        if (isNaN(number) || number < 0 || number > 10080) {
                                            return Promise.reject("Please enter a number between 0 and 10080 (1 week)");
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                            tooltip="Automatically move grandfathered Pending items to Review status after specified minutes (0 = disabled, max 10080 = 1 week)"
                        >
                            <Input
                                placeholder="1440"
                                size="large"
                                type="number"
                                min={0}
                                max={10080}
                            />
                        </Form.Item>

                        {/*
                         * Review → In Process is NOT configured here anymore — its timing is
                         * derived from the "Recent Payment Highlight" hours setting (in the
                         * Unpaid-Cleanup Settings section). When a paid claim's age exceeds
                         * that window, the silent cron promotes it Review → InProcess.
                         *
                         * The legacy reviewToInProcessMinutes column is still in the DB and
                         * still loaded by the cron service for grandfathered claims, but the
                         * admin should no longer tune it from this UI — it has no effect on
                         * the new flow.
                         */}
                        <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 6, padding: 12, marginTop: 8 }}>
                            <div style={{ fontSize: 13, color: "#595959", marginBottom: 8 }}>
                                <strong>Review → In Process timing</strong> is driven by the
                                {" "}<em>Recent Payment Highlight</em> hours under Unpaid Cleanup
                                Settings below.
                            </div>
                            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                                The hourly cron runs at the top of every hour. Click below to run
                                it now (no email is sent to customers).
                            </div>
                            <div style={{ marginTop: 10 }}>
                                <StyledButton
                                    onClick={handleRunReviewToInProcessNow}
                                    loading={runNowLoading}
                                >
                                    Run Review → In Process Now
                                </StyledButton>
                            </div>
                        </div>
                    </FormWrapper>
                </MainFormWrapper>
                <Flex justify="flex-end">
                    <StyledButton
                        type="primary"
                        onClick={handleSave}
                        loading={loading === RequestResults.pending}
                    >
                        Save Settings
                    </StyledButton>
                </Flex>
            </GeneralSettingsContainer>
        </>
    )
}

export default SchedulerSettings
